/**
 * Что консоль показывает владельцу про агентов резервного копирования.
 *
 * Отдельно от того, что видит сам агент (`AgentConfigView` в methods.ts).
 * Формы разные не для порядка: агенту уходит только нужное для работы, а
 * владельцу — состояние, история и то, чем он управляет. Общая форма
 * однажды вынесла бы наружу лишнее.
 */

export type AgentState = 'pending' | 'active' | 'revoked';
export type RunState = 'running' | 'ok' | 'failed' | 'cancelled';
export type RunPhase = 'scan' | 'read' | 'upload' | 'finalize';

export interface AgentRunView {
  runId: string;
  siteId: string;
  state: RunState;
  phase: RunPhase;
  reason: 'schedule' | 'manual' | 'retry';
  sources: string[];
  startedAt: string;
  finishedAt: string | null;
  /** ISO. По нему видно, что проход жив, даже когда числа не меняются. */
  lastActivityAt: string;
  progress: {
    filesSeen: number;
    filesDone: number;
    bytesRead: number;
    bytesUploaded: number;
    chunksTotal: number;
    chunksNew: number;
    chunksReused: number;
    /**
     * Сколько ОЖИДАЕТСЯ — из замера, снятого до прохода. Полоса считается
     * только против этого числа; без него на экране бегущая штриховка.
     *
     * Для дампа базы приходит `bytesTotal`, а не `filesTotal`: файл там один,
     * и число оценочное — размер базы по information_schema расходится с
     * размером дампа примерно в полтора раза.
     */
    filesTotal?: number;
    bytesTotal?: number;
    /**
     * Сколько из прочитанного оказалось различным содержимым.
     *
     * Разрыв между «читает» и «льёт» без него читается как затор, а это
     * работающая дедупликация: прочитанное уже лежит в хранилище.
     */
    bytesDistinct?: number;
    /** Ход сверки: признано совпавшим и отнесено к записи. Идёт ПО ХОДУ. */
    bytesMatched?: number;
    bytesPending?: number;
    /** Ход развёртывания: шаг из скольких и сколько дампа залито. */
    stepDone?: number;
    stepTotal?: number;
    importWritten?: number;
    importTotal?: number;
    /**
     * Чем занят приёмник при восстановлении: `manifest`, `compare`, `chunks`,
     * `restore`. У прохода копирования этого поля нет.
     */
    phase?: 'manifest' | 'compare' | 'chunks' | 'restore';
    /** Фраза приёмника про текущую фазу, если он её прислал. */
    note?: string;
    /**
     * Сетевые байты (сжатые) и записанные (распакованные) — разные числа.
     *
     * Между ними дедупликация и сжатие: на боевом снимке потолок 3,12 байта на
     * диск за байт из сети. Пока приходило одно число, экран показывал его
     * дважды под разными подписями и утверждал, что сеть и диск идут вровень.
     */
    bytesNet?: number;
    bytesWritten?: number;
    netPerSec?: number;
    writtenPerSec?: number;
    packsNeeded?: number;
    packsDone?: number;
    packsDistinct?: number;
    /** Пак, скачанный повторно. В развёрнутой схеме обязан быть нулём. */
    packsRefetched?: number;
    /** Скорости, замеренные агентом за известный промежуток, из одного замера. */
    readPerSec?: number;
    uploadedPerSec?: number;
    /**
     * Залито впустую: паки, не давшие ни одного нового чанка.
     *
     * Отдельным числом, потому что в остальных его не видно: «залито»
     * считает только полезное, и проход с гигабайтом мусора выглядит
     * безупречным.
     */
    bytesRedundant?: number;
    currentPath?: string;
  };
  error: { code: string; msg: string; phase: string } | null;
  snapshotId: string;
  parentSnapshotId: string | null;
}

export interface AgentSiteSummary {
  id: string;
  root: string;
  enabled: boolean;
  requireManifest: boolean;
  retentionDays: number;
  sources: { id: string; enabled: boolean; schedule?: string; verifyEveryNthRun?: number }[];
  /** Последний удачный снимок этого сайта. */
  lastSnapshotAt: string | null;
  /**
   * Итоги последнего снимка: сколько прочитано и сколько из этого оказалось
   * различным.
   *
   * Разница между ними — выигрыш дедупликации, и без неё «залито 83 МБ» не
   * говорит ничего: непонятно, мало это потому что данные повторяются, или
   * потому что проход только начался.
   */
  lastTotals?: {
    files: number;
    bytes: number;
    newBytes?: number;
    uploadedBytes: number;
  };
  /**
   * Исход проверки восстановимости последнего снимка.
   *
   * «Копия снята» и «копия восстановима» — разные утверждения. Первое говорит
   * агент, второе знает только тот, кто прочитал все паки и сверил суммы.
   */
  verify?: {
    state: 'queued' | 'running' | 'ok' | 'failed';
    detail: string | null;
    checked: number;
    total: number;
    finishedAt: string | null;
  };
  lastSnapshotId: string | null;
}

/**
 * Что агент видит на своей машине.
 *
 * После перехода к манифестам консоль раскладки не знает: где корень сайта,
 * какие источники, какие контейнеры подняты. Отчёт — единственное место, где
 * это видно, и без него владелец заводит сайты вслепую.
 */
export interface ReportedSite {
  id: string;
  root: string;
  /** «манифест» или «догадка»: чем агент руководствовался. */
  origin: string;
  enabled?: boolean;
  containers: { name: string; service: string }[];
  sources: {
    id: string; kind: string; where: string; exclude?: string[];
    /** Сколько там всего — до копирования. Пусто: агент ещё не мерил. */
    files?: number; bytes?: number; tables?: number; measuredAt?: string;
  }[];
  gitStatus?: { status: string; detail: string };
  notes?: string[];
  /** Заведён ли он в настройках. Нет — копий по нему не будет. */
  claimed: boolean;
}

/** Состояние сборки машины относительно ветки агента. */
export type AgentBuildState =
  | { kind: 'silent' }
  | { kind: 'no-reference' }
  | { kind: 'current' }
  | { kind: 'behind'; commits: number }
  | { kind: 'foreign' };

export interface HostReportView {
  alias: string;
  hostname: string;
  os: string;
  agentVersion: string;
  /** Коммит развёрнутой сборки, коротким хешем. `null` — агент его не шлёт. */
  buildCommit: string | null;
  /** На машине есть несохранённые правки: развёрнуто не то, что в репозитории. */
  buildDirty: boolean;
  build: AgentBuildState;
  /**
   * Каталоги, куда машина разрешает разворачивать.
   *
   * Описываются в её манифесте: путь, пришедший снаружи, — это команда писать
   * на боевом сервере, и согласие машины должно быть явным. Разрешение не
   * наследуется вложенными каталогами, поэтому список плоский.
   */
  dirs: {
    path: string;
    entries: number;
    /** Объём каталога и когда его мерили: считается в стороне, не в отчёте. */
    bytes?: number;
    bytesAt?: string;
    writable?: boolean;
    /**
     * Чью копию сюда принимают — по объявлению самой машины.
     *
     * Приёмник перечисляет не «куда можно писать», а какие копии готов
     * принять. Визард показывает по нему каталоги ДЛЯ ВОССТАНАВЛИВАЕМОГО
     * САЙТА первыми: рядом с местом для боевого магазина иначе стоят каталог
     * стенда и корень служебного проекта, а опечатка в таком выборе — это
     * восстановление не туда.
     */
    siteId?: string;
    /** Зачем сюда разрешено — словами владельца машины, из её манифеста. */
    why?: string;
    /** Каталог занят развёрнутым проектом, а не просто непуст. */
    looksLikeSite?: boolean;
    /** Занят идущим восстановлением: номер работы и её начало. */
    busy?: { restoreId: string; since: string };
  }[];
  collectedAt: string;
  receivedAt: string;
  sites: ReportedSite[];
  /** Каталоги, которые агент видел и решил не копировать, — со словами почему. */
  notSites: { path: string; reason: string }[];
}

export interface AgentView {
  kid: string;
  hostId: string;
  host: string;
  /** DNS-имя машины: из него выведено имя ведра. */
  dns: string;
  label: string;
  state: AgentState;
  fingerprint: string;
  createdAt: string;
  enrolledAt: string | null;
  lastSeenAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  note?: string;
  /** Прямо сейчас на связи. Не то же, что lastSeenAt: тот про прошлое. */
  online: boolean;
  configVersion: number;
  sites: AgentSiteSummary[];
  /** Идущий проход, если он есть. */
  currentRun: AgentRunView | null;
  /** Последние завершённые — чтобы видеть, что происходит обычно. */
  recentRuns: AgentRunView[];
  /** Что агент рассказал о машине. Пусто — отчёта ещё не было. */
  report: HostReportView | null;
  /** Ведро этой машины: своё у каждой. */
  bucket: string;
}

export interface AgentsSnapshot {
  generatedAt: string;
  agents: AgentView[];
  /** Выданные и ещё не использованные коды. Сам код здесь НЕ показывается. */
  pendingTokens: {
    label: string;
    dns: string;
    bucket: string;
    hostId: string;
    createdAt: string;
    expiresAt: string;
    /** Переустановка на прежний хост, а не новая машина. */
    reinstall: boolean;
  }[];
}

/** Ответ на выдачу кода. Единственное место, где код виден — и один раз. */
export interface IssuedTokenView {
  token: string;
  hostId: string;
  /** DNS-имя машины, как ввёл владелец. */
  dns: string;
  /** Имя ведра, выведенное из него. Показывается сразу: имя решается здесь. */
  bucket: string;
  label: string;
  expiresAt: string;
  reinstall: boolean;
}

/**
 * Живое событие в топике `agents`.
 *
 * Одно объединённое сообщение, а не отдельный топик на каждый случай. Экран
 * всё равно перерисовывает одну и ту же карточку, и три подписки вместо одной
 * означали бы три разных момента, в которые она может разойтись сама с собой.
 */
export type AgentEvent =
  | { kind: 'run.started'; hostId: string; kid: string; run: AgentRunView }
  | { kind: 'run.progress'; hostId: string; kid: string; run: AgentRunView }
  | { kind: 'run.finished'; hostId: string; kid: string; run: AgentRunView }
  | { kind: 'agent.changed'; hostId: string; kid: string; reason: string }
  /**
   * Машина появилась на связи или пропала — без всякого прохода.
   *
   * Отдельным видом, а не полем в `run.*`: связь меняется тогда, когда ничего
   * не происходит, и как раз это важно знать заранее — перед тем, как выбрать
   * машину приёмником для восстановления.
   */
  | { kind: 'agent.presence'; hostId: string; kid: string; online: boolean };

export const AGENTS_TOPIC = 'agents';

/** Топик живых сообщений о восстановлениях. Отдельный от проходов: у экрана
 *  восстановления своя жизнь, и подписывать его на все события агентов
 *  значило бы будить его на каждый кадр чужой работы. */
export const RESTORES_TOPIC = 'restores';

/** Восстановление в том виде, в каком его показывает экран. */
export interface RestoreView {
  restoreId: string;
  snapshotId: string;
  siteId: string;
  targetHostId: string;
  into: string;
  state: 'granted' | 'running' | 'ok' | 'failed' | 'cancelled';
  mode: 'full' | 'resume';
  sources: string[];
  plan: { files: number; dirs: number; bytes: number; chunks: number } | null;
  /**
   * Сколько места на томе приёмника — по его собственному измерению.
   *
   * Наша смета до запуска считает по верхней границе и потому часто пугает
   * зря. Это число другое: его прислал тот, кто будет писать, и с ним экран
   * может показать, как расхождение рассасывается по мере сверки, вместо того
   * чтобы держать владельца на предположении.
   */
  freeBytes: number | null;
  /**
   * Сколько мы ожидали записать, когда выдавали тикет: вес снимка за вычетом
   * того, что прошлая удачная работа сюда уже разложила.
   *
   * Нужно экрану, пока приёмник читает перечни. До конца сверки его
   * `bytes.total` — это вес снимка целиком, и место по нему считается так, как
   * будто диск пуст: «не хватает 83 ГБ» при 33 ГБ настоящей работы.
   */
  expectBytes: number | null;
  /**
   * У работы есть этап развёртывания: поднять службы, залить дамп, перезапустить.
   *
   * Он идёт ПОСЛЕ файлов и о ходе своём не сообщает: у приёмника там 31 шаг
   * подготовки, снятие запасного дампа и заливка на полтора часа. Без этого
   * признака экран объявляет тишину бедой — «работа стоит», — тогда как она
   * идёт, просто рассказывать о ней нечем.
   */
  willDeploy: boolean;
  progress: AgentRunView['progress'];
  requestedAt: string;
  /** Работу прервали, но приёмник обещал поднять её сам — ключи не отозваны. */
  interruptedAt?: string;
  startedAt: string | null;
  finishedAt: string | null;
  lastActivityAt: string;
  expiresAt: string | null;
  error: { code: string; msg: string } | null;
  mismatches: number;
}

export type RestoreEvent =
  | { kind: 'restore.started'; restore: RestoreView }
  | { kind: 'restore.progress'; restore: RestoreView }
  | { kind: 'restore.finished'; restore: RestoreView };
